<template>
  <div>
    <b-card-title class="mb-2">
      {{ $t('settings.coordinates.wgs84.title') }}
    </b-card-title>
    <b-card-text class="mb-3 text-muted">
      {{ $t('settings.coordinates.wgs84.description') }}
    </b-card-text>

    <b-form @submit.prevent="handleSubmit">
      <b-row>
        <!-- Semi Major Axis -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('settings.coordinates.wgs84.semi_major_axis')"
            label-for="wgs84-semi-major"
            :description="$t('settings.coordinates.wgs84.semi_major_axis_desc')"
          >
            <b-form-input
              id="wgs84-semi-major"
              v-model.number="form.semi_major_axis"
              type="number"
              step="0.000001"
              required
            />
          </b-form-group>
        </b-col>

        <!-- Semi Minor Axis -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('settings.coordinates.wgs84.semi_minor_axis')"
            label-for="wgs84-semi-minor"
            :description="$t('settings.coordinates.wgs84.semi_minor_axis_desc')"
          >
            <b-form-input
              id="wgs84-semi-minor"
              v-model.number="form.semi_minor_axis"
              type="number"
              step="0.000001"
              required
            />
          </b-form-group>
        </b-col>

        <!-- Flattening -->
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            :label="$t('settings.coordinates.wgs84.flattening')"
            label-for="wgs84-flattening"
            :description="$t('settings.coordinates.wgs84.flattening_desc')"
          >
            <b-form-input
              id="wgs84-flattening"
              v-model.number="form.flattening"
              type="number"
              step="0.000000000001"
              required
            />
          </b-form-group>
        </b-col>

        <!-- Inverse Flattening -->
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            :label="$t('settings.coordinates.wgs84.inverse_flattening')"
            label-for="wgs84-inverse-flattening"
            :description="$t('settings.coordinates.wgs84.inverse_flattening_desc')"
          >
            <b-form-input
              id="wgs84-inverse-flattening"
              v-model.number="form.inverse_flattening"
              type="number"
              step="0.000001"
              required
            />
          </b-form-group>
        </b-col>

        <!-- Eccentricity Squared -->
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            :label="$t('settings.coordinates.wgs84.eccentricity_squared')"
            label-for="wgs84-eccentricity"
            :description="$t('settings.coordinates.wgs84.eccentricity_squared_desc')"
          >
            <b-form-input
              id="wgs84-eccentricity"
              v-model.number="form.eccentricity_squared"
              type="number"
              step="0.00000001"
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Warning Alert -->
      <b-alert
        show
        variant="warning"
        class="mt-2"
      >
        <div class="alert-body">
          <feather-icon
            icon="AlertTriangleIcon"
            class="mr-50"
          />
          <span>{{ $t('settings.coordinates.wgs84.warning') }}</span>
        </div>
      </b-alert>

      <!-- Actions -->
      <div class="d-flex justify-content-end mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          :disabled="loading"
          @click="resetForm"
        >
          {{ $t('common.reset') }}
        </b-button>
        <b-button
          variant="primary"
          type="submit"
          :disabled="loading"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-50"
          />
          {{ $t('common.save') }}
        </b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import {
  BCardTitle,
  BCardText,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BAlert,
} from 'bootstrap-vue'

export default {
  name: 'Wgs84ParametersForm',
  components: {
    BCardTitle,
    BCardText,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BAlert,
  },
  props: {
    parameters: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        semi_major_axis: 6378137.0,
        semi_minor_axis: 6356752.314245,
        flattening: 0.003352810664,
        inverse_flattening: 298.257223563,
        eccentricity_squared: 0.00669438,
      },
    }
  },
  watch: {
    parameters: {
      immediate: true,
      deep: true,
      handler(newParameters) {
        if (newParameters && Object.keys(newParameters).length > 0) {
          this.form = { ...this.form, ...newParameters }
        }
      },
    },
  },
  methods: {
    handleSubmit() {
      this.$emit('save', { ...this.form })
    },
    resetForm() {
      if (this.parameters && Object.keys(this.parameters).length > 0) {
        this.form = { ...this.parameters }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.mr-50 {
  margin-right: 0.5rem;
}
</style>
